import { Navigate, Route, Routes, useSearchParams } from 'react-router';
import { Spinner, Toaster } from './components/ui.js';
import { useCurrentUser } from './lib/auth.js';
import { safeRedirectPath } from './lib/navigation.js';
import { TeamsProvider, useTeams } from './lib/teams.js';
import { AuthPage } from './pages/AuthPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { TeamPage } from './pages/TeamPage.js';
import { FormatsPage } from './pages/FormatsPage.js';
import { RetroPage } from './pages/RetroPage.js';
import { JoinPage } from './pages/JoinPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { TemplateBuilderPage } from './pages/TemplateBuilderPage.js';
import { AdminPage } from './pages/AdminPage.js';
import { ProfilePage } from './pages/ProfilePage.js';

export function App() {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Loading…" />
      </div>
    );
  }

  return (
    <>
      <TeamsProvider enabled={user !== null}>
        <Routes>
          {/* Invite links must work for signed-out visitors, so this route sits
              outside the auth gate. */}
          <Route path="/join/:token" element={<JoinPage />} />
          {/* Reset links arrive out of band and may be opened while signed in (an
              admin testing their own link), so like invite links this route sits
              outside the auth gate. */}
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/retro/:retroId" element={<RetroPage />} />

          {user ? (
            <>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              {/* Team-neutral aliases so nav links never race the teams
                  query — they redirect once the active team is known. */}
              <Route path="/formats" element={<TeamRedirect suffix="formats" />} />
              <Route path="/team" element={<TeamRedirect suffix="" />} />
              <Route path="/teams/:teamId" element={<TeamPage />} />
              <Route path="/teams/:teamId/formats" element={<FormatsPage />} />
              <Route path="/teams/:teamId/formats/new" element={<TemplateBuilderPage />} />
              <Route path="/teams/:teamId/formats/:templateId" element={<TemplateBuilderPage />} />
              {/* Routed only for admins, and the API answers 404 for everyone
                  else, so guessing the URL reveals nothing. */}
              {user.isAdmin ? <Route path="/admin" element={<AdminPage />} /> : null}
              <Route path="/login" element={<RedirectAfterAuth />} />
              <Route path="/register" element={<RedirectAfterAuth />} />
            </>
          ) : (
            <>
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/register" element={<AuthPage mode="register" />} />
              <Route path="/" element={<Navigate to="/login" replace />} />
            </>
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </TeamsProvider>
      <Toaster />
    </>
  );
}

/**
 * Where to send someone who is signed in but has landed on /login or /register.
 *
 * This has to honour `?next=`, and not for tidiness. Signing in flips `user` from
 * null, which re-renders these routes as a redirect — and that redirect races the
 * `navigate(next)` inside AuthPage and wins. Sending everyone to `/` therefore
 * silently discarded the destination: following a team invite, signing in, and
 * being dropped on the dashboard instead of back at the invitation.
 *
 * The target is sanitised the same way AuthPage does it, so this cannot become an
 * open redirect.
 */
function RedirectAfterAuth() {
  const [searchParams] = useSearchParams();
  return <Navigate to={safeRedirectPath(searchParams.get('next'))} replace />;
}

/**
 * Waits for the teams query and then forwards to the active team's page. This
 * is what the sidebar links point at — building `/teams/:id/…` hrefs straight
 * into the nav would render them as "/" while teams are still loading, and a
 * click in that window goes nowhere.
 */
function TeamRedirect({ suffix }: { suffix: string }) {
  const { teams, activeTeam, isLoading } = useTeams();
  const team = activeTeam ?? teams[0];

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Loading…" />
      </div>
    );
  }
  return <Navigate to={team ? `/teams/${team.id}${suffix ? `/${suffix}` : ''}` : '/'} replace />;
}
